import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// Async thunks
export const fetchMatchFundingData = createAsyncThunk(
  'matchFunding/fetchMatchFundingData',
  async (partnerId, { rejectWithValue }) => {
    try {
      // Simulate API call
      const response = await new Promise((resolve) => {
        setTimeout(() => {
          resolve({
            partner_id: partnerId,
            total_contributions: 450000,
            active_contributions: 45,
            pending_requests: 3,
            funding_history: Array.from({ length: 10 }, (_, i) => ({
              id: `fund_${i + 1}`,
              amount: Math.floor(Math.random() * 100000) + 10000,
              type: ['pool', 'targeted'][Math.floor(Math.random() * 2)],
              status: ['completed', 'pending', 'processing'][Math.floor(Math.random() * 3)],
              created_at: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString(),
              mothers_helped: Math.floor(Math.random() * 20) + 1
            }))
          });
        }, 1000);
      });
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const submitContribution = createAsyncThunk(
  'matchFunding/submitContribution',
  async (contributionData, { rejectWithValue }) => {
    try {
      // Simulate API call
      const response = await new Promise((resolve) => {
        setTimeout(() => {
          resolve({
            id: `fund_${Date.now()}`,
            ...contributionData,
            status: 'completed',
            created_at: new Date().toISOString()
          });
        }, 800);
      });
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  matchFundingData: null,
  isLoading: false,
  error: null,
  lastUpdated: null,
};

const matchFundingSlice = createSlice({
  name: 'matchFunding',
  initialState,
  reducers: {
    clearMatchFundingError: (state) => {
      state.error = null;
    },
    updateMatchFundingData: (state, action) => {
      state.matchFundingData = { ...state.matchFundingData, ...action.payload };
    },
    addContribution: (state, action) => {
      if (state.matchFundingData) {
        state.matchFundingData.funding_history.unshift(action.payload);
        state.matchFundingData.total_contributions += action.payload.amount;
        state.matchFundingData.active_contributions += 1;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Match Funding Data
      .addCase(fetchMatchFundingData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMatchFundingData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.matchFundingData = action.payload;
        state.lastUpdated = Date.now();
        state.error = null;
      })
      .addCase(fetchMatchFundingData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Submit Contribution
      .addCase(submitContribution.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(submitContribution.fulfilled, (state, action) => {
        state.isLoading = false;
        if (state.matchFundingData) {
          state.matchFundingData.funding_history.unshift(action.payload);
          state.matchFundingData.total_contributions += action.payload.amount;
          state.matchFundingData.active_contributions += 1;
        }
        state.lastUpdated = Date.now();
        state.error = null;
      })
      .addCase(submitContribution.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearMatchFundingError,
  updateMatchFundingData,
  addContribution,
} = matchFundingSlice.actions;

// Selectors
export const selectMatchFunding = (state) => state.matchFunding;
export const selectMatchFundingData = (state) => state.matchFunding.matchFundingData;
export const selectMatchFundingLoading = (state) => state.matchFunding.isLoading;
export const selectMatchFundingError = (state) => state.matchFunding.error;
export const selectTotalContributions = (state) => state.matchFunding.matchFundingData?.total_contributions || 0;
export const selectActiveContributions = (state) => state.matchFunding.matchFundingData?.active_contributions || 0;
export const selectFundingHistory = (state) => state.matchFunding.matchFundingData?.funding_history || [];

export default matchFundingSlice.reducer;
